export const TOKEN_STORAGE_KEY = 'local-auth-token';
export const BACKSTAGE_TOKEN_KEY = 'backstage-user-token';

export type AuthUser = {
  username: string;
  displayName: string;
  roles: string[];
};

type AuthResponse = {
  token: string;
  user: AuthUser;
};

type ApiBundleUploadResponse = {
  catalogUrls: string[];
};

function getBackendOrigin() {
  if (typeof window === 'undefined') {
    return 'http://localhost:7007';
  }
  return window.location.port === '3000'
    ? `${window.location.protocol}//${window.location.hostname}:7007`
    : window.location.origin;
}

function getApiUrl(path: string) {
  return `${getBackendOrigin()}/api/local-auth${path}`;
}

function withAuthHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
  };
}

export const authApi = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },
  setToken(token: string) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      localStorage.setItem('local-auth-exp', String(exp));
    } catch {
      // Ignore malformed tokens; caller handles auth failures elsewhere.
    }
  },
  clearToken() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('local-auth-exp');
    localStorage.removeItem(BACKSTAGE_TOKEN_KEY);
  },
  async getBackstageToken(localToken: string): Promise<string> {
    const response = await fetch(`${getBackendOrigin()}/api/auth/localCredentials/refresh`, {
      headers: { Authorization: `Bearer ${localToken}` },
    });
    if (!response.ok) {
      throw new Error('Failed to get Backstage token');
    }
    const body = (await response.json()) as { backstageIdentity: { token: string } };
    return body.backstageIdentity.token;
  },
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await fetch(getApiUrl('/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as
        | { error?: string }
        | null;
      throw new Error(errorBody?.error ?? 'Login failed');
    }
    return response.json();
  },
  async signup(username: string, password: string, displayName?: string): Promise<AuthResponse> {
    const response = await fetch(getApiUrl('/signup'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, displayName }),
    });
    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as
        | { error?: string }
        | null;
      throw new Error(errorBody?.error ?? 'Signup failed');
    }
    return response.json();
  },
  async createUserByAdmin(
    token: string,
    username: string,
    password: string,
    isAdmin: boolean,
    displayName?: string,
  ): Promise<{ user: AuthUser }> {
    const response = await fetch(getApiUrl('/admin/users'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...withAuthHeaders(token),
      },
      body: JSON.stringify({ username, password, displayName, isAdmin }),
    });
    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as
        | { error?: string }
        | null;
      throw new Error(errorBody?.error ?? 'Failed to create user');
    }
    return response.json();
  },
  async listUsersByAdmin(token: string): Promise<{ users: AuthUser[] }> {
    const response = await fetch(getApiUrl('/admin/users'), {
      headers: withAuthHeaders(token),
    });
    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as
        | { error?: string }
        | null;
      throw new Error(errorBody?.error ?? 'Failed to load users');
    }
    return response.json();
  },
  async getCurrentUser(token: string): Promise<AuthUser> {
    const response = await fetch(getApiUrl('/me'), {
      headers: withAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error('Session expired');
    }
    const body = (await response.json()) as { user: AuthUser };
    return body.user;
  },
  async updateProfile(token: string, displayName: string): Promise<{ token: string; user: AuthUser }> {
    const response = await fetch(getApiUrl('/me'), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...withAuthHeaders(token),
      },
      body: JSON.stringify({ displayName }),
    });
    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(errorBody?.error ?? 'Failed to update profile');
    }
    return response.json();
  },
  async uploadApiBundle(token: string, file: File): Promise<ApiBundleUploadResponse> {
    const formData = new FormData();
    formData.append('bundle', file);

    const response = await fetch(getApiUrl('/api-bundles/upload'), {
      method: 'POST',
      headers: withAuthHeaders(token),
      body: formData,
    });

    if (!response.ok) {
      const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(errorBody?.error ?? 'Failed to upload API bundle');
    }

    return response.json();
  },
};